import { findObjectUseCase } from "../../../usecases/object";

/**
 * Fetch all schedule assignments for a month (created <= endOfMonthIso), sorted by created desc.
 * Used when processing multiple logs: for each log day D, pick the assignment where created <= D.
 */
export async function getEffectiveScheduleAssignmentsForMonth(
  employeeId,
  endOfMonthIso,
) {
  if (!employeeId) return [];
  // console.log("endOfMonthIso: ", endOfMonthIso);
  try {
    const results = await findObjectUseCase().execute(
      "selected_schedule_assignment",
      {
        where: {
          employeeId: employeeId,
          // created: { $lte: endOfMonthIso },
        },
        keys: [
          "scheduleArray",
          "name",
          "selectedOption",
          "sequence",
          "startingDate",
          "created",
        ],
        sort: { created: -1 },
        limit: 5,
      },
    );
    return Array.isArray(results) ? results : [];
  } catch (e) {
    console.error("Error fetching effective schedule assignments:", e);
    return [];
  }
}

/**
 * Get the effective schedule template assignment for a specific day D.
 * Queries schedule_template_assignment where:
 *   - parentId = scheduleId
 *   - created <= D (most important)
 * Sorts by created descending and returns the first (most recent) record.
 * This ensures schedule changes are applied only after their creation date.
 */
export async function getEffectiveScheduleForDay(
  employeeId,
  startOfMonthIso,
  endOfMonthIso,
) {
  if (!employeeId) return null;

  try {
    // Use end-of-day so assignments created on this day are included.
    // We want: "most recent assignment where created <= end of day D".
    // So Dec 14 uses only assignments created on or before Dec 14; Dec 15 onward uses the one created Dec 15.
    // const dateKey = date.toISOString().split("T")[0];
    // const endOfDay = `${dateKey}T23:59:59.999Z`;

    const results = await findObjectUseCase().execute(
      "selected_schedule_assignment",
      {
        where: {
          employeeId: employeeId,
          created: { $lte: endOfMonthIso, $gte: startOfMonthIso },
        },
        keys: [
          "scheduleArray",
          "name",
          "selectedOption",
          "sequence",
          "startingDate",
          "created",
        ],
        options: {
          sort: { created: -1 }, // newest first (support either field)
          limit: 2,
        },
      },
    );
    // console.log("stampDate: ", date);
    console.log("effectiveSchedule: ", results);

    return Array.isArray(results) && results.length > 0 ? results[0] : null;
  } catch (e) {
    console.error("Error fetching effective schedule:", e);
    return null;
  }
}

/**
 * N-on / M-off cycle formula (same for Regular and Graveyard):
 *   cycleDay (1-based) <= N  →  work day / work night
 *   otherwise                →  off
 *
 * Graveyard OUT on the morning after the last work night is NOT an extra
 * scheduled day here — BiometricDTR treats it as timeout-only carry-over
 * when the previous calendar day was a work night.
 */
function cycleWorkDays(selectedOption) {
  if (!selectedOption) return null;
  if (selectedOption.includes("3days Working")) return 3;
  if (selectedOption.includes("4days Working")) return 4;
  return null;
}

const CYCLE_LENGTH = 6;

/** UTC noon avoids weekday/DST shifts when comparing calendar days. */
function toUtcNoon(date) {
  if (!date) return null;
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return new Date(`${date.slice(0, 10)}T12:00:00.000Z`);
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12),
  );
}

/**
 * Cycle position for N-on/M-off schedules.
 * Returns null when the assignment is not a 6-day cycle option.
 */
export function getCycleDayInfo(employeeSchedule, date) {
  const workDays = cycleWorkDays(
    employeeSchedule?.selectedOption ?? employeeSchedule?.name,
  );
  if (workDays == null) return null;

  const cycleBasedDate = toUtcNoon(employeeSchedule?.startingDate);
  const dayDate = toUtcNoon(date);
  if (!cycleBasedDate || !dayDate) return null;

  const dayDifference = Math.floor(
    (dayDate - cycleBasedDate) / (1000 * 60 * 60 * 24),
  );
  if (dayDifference < 0) return { workDays, cycleDay: null, isWorkDay: false };

  const cycleDay = (dayDifference % CYCLE_LENGTH) + 1;
  return {
    workDays,
    cycleDay,
    isWorkDay: cycleDay <= workDays,
  };
}

export function getScheduleForDay(employeeSchedule, date) {
  const dayDate = toUtcNoon(date) ?? new Date(date);
  const weekday = dayDate.toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });

  const cycle = getCycleDayInfo(employeeSchedule, dayDate);
  const workDays = cycle?.workDays ?? null;

  let scheduleForDay = null;

  switch (employeeSchedule?.sequence) {
    case "10.5 HOURS":
      if (workDays != null) {
        if (cycle?.isWorkDay) {
          scheduleForDay = employeeSchedule?.scheduleArray?.[0] ?? null;
        }
      } else {
        scheduleForDay = employeeSchedule?.scheduleArray?.find(
          (s) => s.day === weekday,
        );
      }
      break;
    case "9/12/12.5/13 HOURS":
      if (workDays != null) {
        // One formula: Regular/Graveyard 3-on/3-off or 4-on/2-off
        // Work iff cycleDay <= N (never treat graveyard final-morning as work)
        if (cycle?.isWorkDay) {
          scheduleForDay = employeeSchedule?.scheduleArray?.[0] ?? null;
        }
      } else if (employeeSchedule?.selectedOption === "Graveyard") {
        for (const sched of employeeSchedule?.scheduleArray || []) {
          // Day-based graveyard: single day (e.g. "Friday") = shift starts that night
          if (!sched.day?.includes(" - ")) {
            if (weekday === sched.day) {
              scheduleForDay = sched;
              break;
            }
            continue;
          }
          const [startDay, endDay] = sched.day.split(" - ");
          if (weekday === startDay || weekday === endDay) {
            scheduleForDay = sched;
            break;
          }
        }
      } else {
        scheduleForDay = employeeSchedule?.scheduleArray?.find(
          (s) => s.day === weekday,
        );
      }
      break;
    case "24 HOURS":
      for (const sched of employeeSchedule?.scheduleArray || []) {
        const [startDay, endDay] = sched.day.split(" - ");
        if (weekday === startDay || weekday === endDay) {
          scheduleForDay = sched;
          break;
        }
      }
      break;

    default:
      scheduleForDay = employeeSchedule?.scheduleArray?.find(
        (s) => s.day === weekday,
      );
      break;
  }

  return scheduleForDay;
}
